import { RequestStatus } from 'features/request/requestSlice';
import type {
    IContractOtherCostLimitSpanSelectionItem,
    IUerContractOtherCostLimitSpanSelectionData,
} from 'pages/Project/UerProject/Contract/hooks/useUerContractOtherCostLimitSpanSelectionList';
import { formatNumberToRu } from 'utils';
import { replacerCost } from 'utils/replacerCost';

import type { GetRowIdParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import * as R from 'ramda';
import type React from 'react';
import { useCallback, useEffect, useState, useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import type { UseFormSetValue } from 'react-hook-form/dist/types/form';

import { FieldNumberFormat } from '../FieldNumberFormat';
import { ModalDialog } from '../ModalDialog';
import { TooltipWrapper } from '../TooltipWrapper';

import { useUerContractOtherCostLimitSpanSelectionListColumns } from './hooks/useUerContractOtherCostLimitSpanSelectionListColumns';

import Styles from './ContractOtherCostLimitSpan.module.css';

interface IContractOtherCostLimitSpanModal {
    constructionTypeId: number;
    constructionTypeName: string;
    onClose: () => void;
    name: string;
    control: Control<any>;
    costRounding: number;
    rules: any;
    setValue: UseFormSetValue<any>;
    spanSelectionData: IUerContractOtherCostLimitSpanSelectionData;
}

const getRowId = (params: GetRowIdParams) => params.data.id;

const gridOptions = {
    headerHeight: 40,
};

export const ContractOtherCostLimitSpanModal: React.FC<IContractOtherCostLimitSpanModal> = (props) => {
    const {
        constructionTypeName,
        onClose,
        name,
        costRounding,
        setValue,
        spanSelectionData: { allIds, status, data, totalsRow, collapseState, changeCollapseState },
        control,
        constructionTypeId,
    } = props;

    const { clearErrors } = useFormContext();

    const title = useMemo(
        () => `Перераспределение лимитов по договору для вида строительства «${constructionTypeName}»`,
        [constructionTypeName],
    );

    const [checkedIds, setCheckedIds] = useState<string[]>([]);
    const [costsTotal, setCostsTotal] = useState<number>(0);
    const [tempCost, setTempCost] = useState<string>();
    const [tempSpanCosts, setTempSpanCost] = useState<Record<string, number | null>>({});
    const [tempLimitSpanInputs, setTempLimitSpanInputs] = useState<IContractOtherCostLimitSpanSelectionItem[]>([]);

    const formLimitSpanInputs: IContractOtherCostLimitSpanSelectionItem[] | undefined = useWatch({
        control,
        name: `limitSpanInputs.${constructionTypeId}`,
    });
    const formCostValue = useWatch({ control, name: `cost.${constructionTypeId}` });

    const hasRemaindersErrors = useMemo(
        () =>
            !!Object.keys(tempSpanCosts).find((key) => {
                const spanDataValue = data?.find((d) => d?.id === key)?.reminder ?? 0;

                const seasonWithValue = !checkedIds.includes(key) && tempSpanCosts[key];

                if (seasonWithValue) {
                    return (tempSpanCosts[key] ?? 0) > spanDataValue;
                }

                return checkedIds.includes(key) && (tempSpanCosts[key] ?? 0) > spanDataValue;
            }),
        [checkedIds, data, tempSpanCosts],
    );

    const showMessage = useMemo(() => replacerCost(tempCost) !== costsTotal, [costsTotal, tempCost]);

    const rowsData = useMemo(
        () =>
            data
                .filter((row) => row.constructionTypeId === constructionTypeId)
                .map((row) =>
                    typeof tempSpanCosts[row.id] !== 'undefined' ? { ...row, cost: tempSpanCosts[row.id] } : row,
                ),
        [constructionTypeId, data, tempSpanCosts],
    );

    const onSpanCostChange = useCallback((id: string, value: number | null, parentKey?: string) => {
        if (id) {
            setTempSpanCost((prev) => {
                if (prev[id] === value) {
                    return prev;
                }

                if (parentKey) {
                    const childKeys = Object.keys(prev).filter((key) => key.startsWith(`${parentKey}.`));

                    const sum =
                        childKeys.every((k) => k !== id && prev[k] === null) && value === null
                            ? null
                            : R.sum(childKeys.map((key) => (key !== id ? prev[key] || 0 : value || 0)));

                    return { ...prev, [id]: value, [parentKey]: sum };
                }

                return { ...prev, [id]: value };
            });
        }
    }, []);

    const onCostChange = useCallback(({ value }) => {
        setTempCost(value);
    }, []);

    const onConfirm = useCallback(() => {
        const limitsToSet = tempLimitSpanInputs.map((input) => ({
            ...input,
            cost: replacerCost(tempSpanCosts[input.id]),
        }));

        setValue(`cost.${constructionTypeId}`, tempCost, { shouldDirty: true });
        setValue(`limitSpanInputs.${constructionTypeId}`, limitsToSet, { shouldDirty: true });
        clearErrors(`cost.${constructionTypeId}`);

        onClose();
    }, [clearErrors, constructionTypeId, onClose, setValue, tempCost, tempLimitSpanInputs, tempSpanCosts]);

    const columns = useUerContractOtherCostLimitSpanSelectionListColumns({
        checkedIds,
        onCheckChange: setCheckedIds,
        allIds,
        onChange: onSpanCostChange,
        collapseState,
        changeCollapseState,
    });

    const btnConfirmDisabled = useMemo(
        () => status === RequestStatus.FETCHING || showMessage || !tempCost || hasRemaindersErrors,
        [hasRemaindersErrors, showMessage, status, tempCost],
    );

    useEffect(() => {
        const toSum = Object.keys(tempSpanCosts)
            .filter((k) => !k.includes('.') && tempSpanCosts[k] !== null)
            .map((k) => tempSpanCosts[k]);

        setCostsTotal(R.sum(toSum as number[]));
    }, [tempSpanCosts]);

    useEffect(() => {
        if (formLimitSpanInputs?.length) {
            const dataToSet = formLimitSpanInputs.reduce(
                (acc, input) => {
                    acc[input.id] = input.cost;

                    return acc;
                },
                {} as Record<string, number>,
            );

            setTempSpanCost(dataToSet);

            setCheckedIds(
                formLimitSpanInputs
                    .filter((item) => item.isGroup || (item.spanMember && !item.isGroupMember))
                    .map((item) => item.id),
            );
        }
    }, [formLimitSpanInputs]);

    useEffect(() => {
        const tempData = data?.filter((item) => {
            if (item.isGroupMember) {
                return checkedIds.includes(item.groupKey as string);
            }

            return checkedIds.includes(item.id as string);
        });

        setTempLimitSpanInputs(tempData);
    }, [checkedIds, data]);

    useEffect(() => {
        setTempCost(formCostValue);
    }, [formCostValue]);

    return (
        <ModalDialog
            isOpen
            isOnClickOutsideDisable
            hasOverlay
            withTitleCloseButton
            title={title}
            onClose={onClose}
            onConfirm={onConfirm}
            isCommit={false}
            btnConfirmDisabled={btnConfirmDisabled}
            className={Styles.modal}
        >
            <div className={Styles.modalBody}>
                <div className={Styles.headerBody}>
                    <div className={Styles.headerBodyBar}>
                        <div className={Styles.blockWithEllipsis}>{name}</div>
                    </div>
                    <div className={Styles.headerBodyBar}>
                        <FieldNumberFormat
                            name="tempLimitСost"
                            label="Лимит по Договору"
                            labelPosition="left"
                            type="text"
                            size="s"
                            required
                            decimalScale={costRounding || 0}
                            placeholder="Введите"
                            className={Styles.costModalInput}
                            value={tempCost}
                            onChange={onCostChange}
                        />
                    </div>
                    <div className={Styles.headerBodyBar}>
                        <div>Сумма лимитов</div>
                        <TooltipWrapper
                            message="Сумма введенных значений стоимости должна быть равна значению лимиту добавленной прочей"
                            size="m"
                            isInteractive={false}
                            isHidden={!showMessage}
                            status="alert"
                            direction="downCenter"
                        >
                            <div className={`${Styles.costsTotal} ${showMessage ? Styles.costsTotalAlert : undefined}`}>
                                {formatNumberToRu(costsTotal, 2)} ₽
                            </div>
                        </TooltipWrapper>
                    </div>
                </div>
                <div className={`${Styles.gridBody} ag-theme-alpine customHeaderColor BimWorkOrderPrice`}>
                    <AgGridReact
                        rowData={rowsData}
                        columnDefs={columns}
                        overlayNoRowsTemplate="Ничего не найдено"
                        pinnedBottomRowData={[totalsRow]}
                        getRowId={getRowId}
                        gridOptions={gridOptions}
                        tooltipShowDelay={400}
                    />
                </div>
            </div>
        </ModalDialog>
    );
};

ContractOtherCostLimitSpanModal.displayName = 'ContractOtherCostLimitSpanModal';
